import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { Masks, MaskService } from '../../../../shared/services/mask.service';
import { TabelaIbptService } from '../../../../shared/services/apis/motor-fiscal/tabela-ibpt.service';
import { AlterarCredencialIbptRequest } from '../../../../shared/services/requests/motor-fiscal/alterar-credencial-ibpt.request';
import { ReturnStruct } from '../../../../shared/services/structs/return.struct';
import { cnpjValidator } from '../../../../shared/validators/cnpj.validator';

export interface AlterarCredencialIbptDialogData {
  cnpj: string | null;
  finalToken: string | null;
}

/**
 * Diálogo "Alterar CNPJ e token". O par é testado no IBPT pela API antes de gravar; em falha o diálogo continua
 * aberto e os dados atuais são mantidos. Fecha com true só quando a API gravou.
 */
@Component({
  selector: 'app-alterar-credencial-ibpt-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './alterar-credencial-ibpt-dialog.component.html',
  styleUrl: './alterar-credencial-ibpt-dialog.component.css',
})
export class AlterarCredencialIbptDialogComponent {
  public model: FormGroup;
  public masks: Masks;
  public isTestando: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AlterarCredencialIbptDialogData,
    private dialogRef: MatDialogRef<AlterarCredencialIbptDialogComponent, boolean>,
    private formBuilder: FormBuilder,
    private maskService: MaskService,
    private tabelaIbptService: TabelaIbptService,
    private alertService: AlertService
  ) {
    this.masks = this.maskService.getMasks();
    this.model = this.formBuilder.group({
      cnpj: [data?.cnpj ?? '', [Validators.required, cnpjValidator()]],
      // Obrigatório inclusive quando só o CNPJ muda: o token do IBPT é emitido para um CNPJ.
      token: ['', [Validators.required]],
    });
  }

  public cancelar(): void {
    this.dialogRef.close(false);
  }

  public testarESalvar(): void {
    if (this.model.invalid) {
      this.model.markAllAsTouched();
      return;
    }

    const request: AlterarCredencialIbptRequest = {
      cnpj: (this.model.get('cnpj')?.value as string).toUpperCase(),
      token: (this.model.get('token')?.value as string).trim(),
    };

    this.isTestando = true;
    this.dialogRef.disableClose = true;
    this.tabelaIbptService.alterarCredencial(request).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this.isTestando = false;
        this.dialogRef.disableClose = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.dialogRef.close(true);
      },
      error: (error: any) => {
        this.isTestando = false;
        this.dialogRef.disableClose = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }
}
